import { Cloche } from '../components/Cloche'
import { LogoComplet } from '../components/Icones'
import { useEffect, useState } from 'react'
import { useHistorique } from '../lib/historique'
import { supabase } from '../lib/supabase'
import { useAuth, estAdmin } from '../lib/auth'
import { EtatSync } from '../components/EtatSync'
import type { Chantier } from '../lib/types'
import { NouveauChantier } from './NouveauChantier'

export function Chantiers({
  ouvrir,
  aller
}: {
  ouvrir: (c: Chantier) => void
  aller: (chantierId: string, suiviId: string | null) => void
}) {
  const { profil, deconnexion } = useAuth()
  const [chantiers, setChantiers] = useState<Chantier[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [creation, setCreation] = useState(false)
  useHistorique(creation, setCreation)

  async function charger() {
    // Ordre fixe, par numéro : chacun retrouve son chantier toujours à la
    // même place (décision d'ergonomie no 5). Pas de tri par activité récente.
    const { data, error } = await supabase
      .from('chantiers')
      .select('*')
      .order('numero', { ascending: true })
    if (error) setErreur(error.message)
    else setChantiers((data as Chantier[]) ?? [])
  }

  useEffect(() => {
    void charger()
  }, [])

  if (creation) {
    return (
      <NouveauChantier
        fermer={() => setCreation(false)}
        cree={() => {
          setCreation(false)
          void charger()
        }}
      />
    )
  }

  const actifs = (chantiers ?? []).filter((c) => c.statut !== 'termine')
  const archives = (chantiers ?? []).filter((c) => c.statut === 'termine')

  return (
    <div className="app">
      <header className="entete">
        <div className="entete-logo logo-centre">
          <LogoComplet hauteur={48} />
        </div>
        <div className="entete-rang">
          <div style={{ flex: 1 }}>
            <h1>Mes chantiers</h1>
            <p className="sous">
              {profil?.nom} · {libelleRole(profil?.role)}
            </p>
          </div>
          <EtatSync />
          <Cloche aller={aller} />
        </div>
      </header>

      <div className="corps">
        {erreur && <p className="avis erreur">{erreur}</p>}

        {chantiers === null && <p className="vide">Chargement…</p>}

        {chantiers !== null && actifs.length === 0 && archives.length === 0 && (
          <p className="vide">
            Aucun chantier ne t'est assigné pour l'instant.
            <br />
            Un administrateur doit t'ajouter à un chantier.
          </p>
        )}

        {actifs.length > 0 && (
          <>
            <div className="sect">
              Actifs <span className="code">Ordre fixe · par numéro</span>
            </div>
            <div className="liste">
              {actifs.map((c) => (
                <button key={c.id} className="carte" onClick={() => ouvrir(c)}>
                  <div className="entre">
                    <div>
                      <span className="code">{c.numero}</span>
                      <p className="titre">{c.nom}</p>
                    </div>
                    {c.statut === 'suspendu' && <span className="pastille p-neutre">Suspendu</span>}
                  </div>
                  <div className="jetons">
                    {c.adresse && <span className="jeton">{c.adresse}</span>}
                    {c.entrepreneur_general && (
                      <span className="jeton">{c.entrepreneur_general}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {archives.length > 0 && (
          <>
            <div className="sect">
              Archivés <span className="code">Lecture seule</span>
            </div>
            <div className="liste">
              {archives.map((c) => (
                <button
                  key={c.id}
                  className="carte"
                  style={{ opacity: 0.72 }}
                  onClick={() => ouvrir(c)}
                >
                  <div className="entre">
                    <div>
                      <span className="code">{c.numero}</span>
                      <p className="titre">{c.nom}</p>
                    </div>
                    <span className="pastille p-ferme">Archivé</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {estAdmin(profil) && (
          <button className="bouton" style={{ marginTop: 22 }} onClick={() => setCreation(true)}>
            Créer un chantier
          </button>
        )}

        <button
          className="bouton creux"
          style={{ marginTop: 10 }}
          onClick={() => void deconnexion()}
        >
          Se déconnecter
        </button>

        <p className="code" style={{ textAlign: 'center', marginTop: 18, opacity: 0.7 }}>
          Version du {__VERSION__}
        </p>
      </div>
    </div>
  )
}

function libelleRole(r: string | undefined): string {
  if (r === 'admin') return 'administrateur'
  if (r === 'chef') return "chef d'équipe"
  return 'consultation'
}
